/**
 * Script super-section grouper + live-script label decorator.
 *
 * Purpose
 * -------
 * The "2026 Phone Sales Script V2.0.1" stores its content as a long flat
 * array of `sales_scripts.script_sections[]` rows where each individual
 * "Section Description", "Important Info", "Actual Script Pathway",
 * objection card, and "End Of Section" is its own row. That makes the
 * left-nav unusable (172 generic items) and the rendered view a wall of
 * undifferentiated sub-blocks.
 *
 * The actual super-section header lives inside the row content as
 * `<h1>1. Pre-Presentation Objections</h1>`, `<h1>B1. Objection Loops…</h1>`,
 * etc. This module:
 *
 *   1. Parses the flat sections array and groups rows into "super sections"
 *      delimited by those H1 markers (`^(\d+[A-Z]?|B\d+)\.\s+…`).
 *   2. Classifies each child row into one of: setup / live / card / exit / loop
 *      based on its `title`. Generic titles ("Section Description",
 *      "Actual Script Pathway", "End Of Section", "Card 4F: …", "Loop 1: …")
 *      drive the bucketing.
 *   3. Returns `null` when the pattern doesn't match (e.g. the Florida 2026
 *      script which already has clean named sections) so callers fall back
 *      to the existing flat ScriptSectionNav + per-section rendering.
 *
 * The decorator (`decorateScriptLabels`) is independent of grouping and is
 * safe to run on any rendered script HTML — it prepends the
 * SAY THIS / WAIT / DO THIS / TRIGGER / RETURN TO / NEXT / AGENT ONLY
 * highlight tokens in front of the existing bracket labels (`[Rep]`,
 * `[Customer Response]`, etc.) so reps can scan the live lane visually
 * without touching the source text. Bracket placeholders like `[Address]`
 * are also bolded.
 */

export interface RawSection {
  type: string;
  title: string;
  content: string;
}

export type SuperChildGroup = "setup" | "live" | "card" | "exit" | "loop" | "other";

export interface SuperChild {
  /** Original index in the flat sections array (used for scroll/select). */
  flatIndex: number;
  rawTitle: string;
  /** Classified bucket. */
  group: SuperChildGroup;
  /** Sanitized inner HTML (super-header H1s stripped). */
  html: string;
}

export interface SuperSection {
  /** Stable id for nav anchors. */
  id: string;
  /** Raw header text e.g. "1. Pre-Presentation Objections" or "B1. Objection Loops and Closing Weapons". */
  title: string;
  /** Numeric/alpha prefix e.g. "1", "10B", "B1". */
  prefix: string;
  /** Title without the leading prefix (e.g. "Pre-Presentation Objections"). */
  shortTitle: string;
  /** True for the B1 (loops) super-section — driven by loop children, not cards. */
  isLoops: boolean;
  children: SuperChild[];
}

const SUPER_HEADER_RE = /^(\d+[A-Z]?|B\d+)\.\s+(.+)$/;

/**
 * Bucket a raw row by its title. Order matters — "Card 1A …" must hit "card"
 * before falling through to "other".
 */
function classifyRowTitle(title: string): SuperChildGroup {
  const t = title.trim();
  const lower = t.toLowerCase();
  if (/^section$/i.test(t)) return "setup"; // pure header rows; content's H1 already split off
  if (/^section description/i.test(t)) return "setup";
  if (/^important info/i.test(t)) return "setup";
  if (/\[agent only\]/i.test(t) && !/notes/i.test(lower)) return "setup";
  if (/^actual script pathway/i.test(t)) return "live";
  if (/^card\b/i.test(t)) return "card";
  if (/^loop\b/i.test(t)) return "loop";
  if (/^(the deflection loop|increase pain threshold|lower action threshold)/i.test(t)) return "loop";
  if (/^section notes/i.test(t)) return "exit";
  if (/^end of section/i.test(t)) return "exit";
  return "other";
}

function isEmptyHtml(html: string): boolean {
  if (!html) return true;
  const stripped = html.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();
  return stripped.length === 0;
}

interface SplitResult {
  /** Content fragments delimited by detected H1 super-headers. */
  fragments: { headerBefore: string | null; html: string }[];
}

/**
 * Split a single row's HTML at H1 elements whose text matches
 * `SUPER_HEADER_RE`. Returns an ordered list of {headerBefore, html}
 * fragments where `headerBefore === null` means "continue the previous
 * super-section" and a non-null value means "this fragment belongs to a
 * new super-section titled `headerBefore`". The H1 itself is consumed.
 */
function splitOnSuperHeaders(html: string): SplitResult {
  if (typeof window === "undefined" || !html.includes("<h1")) {
    return { fragments: [{ headerBefore: null, html }] };
  }
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return { fragments: [{ headerBefore: null, html }] };

  const fragments: { headerBefore: string | null; html: string }[] = [];
  let current = doc.createElement("div");
  let headerForCurrent: string | null = null;

  const flush = () => {
    if (current.childNodes.length > 0 || headerForCurrent !== null) {
      fragments.push({ headerBefore: headerForCurrent, html: current.innerHTML });
    }
  };

  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === 1 && (node as HTMLElement).tagName === "H1") {
      const text = ((node as HTMLElement).textContent || "").replace(/\s+/g, " ").trim();
      if (SUPER_HEADER_RE.test(text)) {
        flush();
        current = doc.createElement("div");
        headerForCurrent = text;
        continue;
      }
    }
    current.appendChild(node.cloneNode(true));
  }
  flush();
  if (fragments.length === 0) fragments.push({ headerBefore: null, html });
  return { fragments };
}

function slugify(text: string, idx: number): string {
  const base = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
  return `super-${idx}-${base || "section"}`;
}

/**
 * Group a flat sections array into hierarchical super-sections. Returns
 * `null` if no H1 super-headers are detected (caller should fall back to
 * the flat layout).
 */
export function groupSuperSections(sections: RawSection[] | undefined | null): SuperSection[] | null {
  if (!sections || sections.length === 0) return null;
  if (typeof window === "undefined") return null;

  // Quick pre-scan: bail if no super-header pattern present anywhere.
  const hasAnyHeader = sections.some((s) =>
    /<h1[^>]*>\s*<?[^<]*?(?:\d+[A-Z]?|B\d+)\.\s/i.test(s.content || "")
  );
  if (!hasAnyHeader) return null;

  const supers: SuperSection[] = [];
  let current: SuperSection | null = null;

  const openSuper = (rawHeader: string) => {
    const match = rawHeader.match(SUPER_HEADER_RE);
    const prefix = match ? match[1] : "";
    const shortTitle = match ? match[2].trim() : rawHeader;
    const idx = supers.length;
    const sup: SuperSection = {
      id: slugify(rawHeader, idx),
      title: rawHeader,
      prefix,
      shortTitle,
      isLoops: /^B\d+$/i.test(prefix),
      children: [],
    };
    supers.push(sup);
    current = sup;
  };

  sections.forEach((row, flatIndex) => {
    const { fragments } = splitOnSuperHeaders(row.content || "");
    fragments.forEach((frag, fi) => {
      // First fragment may continue the previous super; subsequent fragments
      // each open a new super-section.
      if (frag.headerBefore) openSuper(frag.headerBefore);
      if (isEmptyHtml(frag.html)) return;
      // If we have no current super yet (content before any header), drop it
      // into a synthetic "Intro" super so it isn't lost.
      if (!current) openSuper("0. Script Intro");
      current!.children.push({
        flatIndex,
        rawTitle: row.title,
        group: classifyRowTitle(row.title),
        html: frag.html,
      });
    });
  });

  if (supers.length <= 1) return null; // not enough structure to justify grouping
  return supers;
}

// ---------------------------------------------------------------------------
// Label decorator
// ---------------------------------------------------------------------------

interface LabelSpec {
  /** Bracket text exactly as it appears in source (without surrounding spaces). */
  bracket: string;
  /** Visible label token to prepend. */
  token: string;
  /** Highlight background color (hex matching editor palette). */
  color: string;
  italic?: boolean;
}

const TRIGGER_RE = /^(\s*)Trigger:/i;

const LABEL_SPECS: LabelSpec[] = [
  { bracket: "[Rep]", token: "SAY THIS", color: "#fef08a" }, // yellow
  { bracket: "[Customer Response]", token: "WAIT", color: "#bae6fd" }, // sky
  { bracket: "[Customer]", token: "WAIT", color: "#bae6fd" }, // sky (legacy label variant)
  { bracket: "[Agent Action]", token: "DO THIS", color: "#a7f3d0" }, // mint
  { bracket: "[Return To Script]", token: "RETURN TO", color: "#ddd6fe" }, // violet
  { bracket: "[Next Section]", token: "NEXT", color: "#d9f99d" }, // lime
  { bracket: "[Notes - Do Not Read]", token: "AGENT ONLY", color: "#fbcfe8", italic: true }, // pink
];

const LABEL_BRACKETS = new Set(LABEL_SPECS.map((s) => s.bracket.toLowerCase()));

/**
 * Customer-response classifier. True when the given text represents a line
 * the CUSTOMER is expected to say, i.e. it carries a `[Customer Response]`
 * or `[Customer]` label. Whitespace tolerant and case-insensitive; works on
 * plain text extracted from the DOM so `<strong>[Customer]</strong>` and
 * `<em>[Customer Response]</em>` are detected identically.
 */
export function isCustomerResponseLine(text: string | null | undefined): boolean {
  if (!text) return false;
  return /\[\s*customer(\s+response)?\s*\]/i.test(text);
}

const CUSTOMER_BLOCK_TAGS = new Set([
  "P", "LI", "BLOCKQUOTE", "DIV", "TD", "TH", "TR", "H1", "H2", "H3", "H4", "H5", "H6", "SECTION",
]);

/**
 * Tag the innermost block element of every customer-response line with
 * `data-script-customer="1"` so the "Hide Customer Responses" toggle can
 * remove the whole line (not just the label token) with a single CSS rule.
 * Render-time only; source text is never modified.
 */
export function markCustomerBlocks(root: HTMLElement): void {
  const all = Array.from(root.querySelectorAll<HTMLElement>("*"));
  const candidates = all.filter(
    (el) => CUSTOMER_BLOCK_TAGS.has(el.tagName) && isCustomerResponseLine(el.textContent),
  );
  for (const el of candidates) {
    const hasBlockDescendant = candidates.some((other) => other !== el && el.contains(other));
    if (hasBlockDescendant) continue;
    el.setAttribute("data-script-customer", "1");
  }
}

/**
 * Convenience wrapper: mark customer-response blocks in an HTML string.
 * Used by tests and by any renderer that needs the tagging without the
 * full label decoration pass.
 */
export function markCustomerResponseHtml(html: string): string {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  markCustomerBlocks(wrapper);
  return wrapper.innerHTML;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function buildLabelMark(spec: LabelSpec): string {
  const style = `background-color:${spec.color};padding:0 4px;border-radius:3px;font-size:0.7rem;letter-spacing:0.04em;`;
  const inner = spec.italic
    ? `<strong><em>${spec.token}</em></strong>`
    : `<strong>${spec.token}</strong>`;
  return `<mark data-script-label="${spec.token}" style="${style}">${inner}</mark> `;
}

function buildTriggerMark(): string {
  return `<mark data-script-label="TRIGGER" style="background-color:#fed7aa;padding:0 4px;border-radius:3px;font-size:0.7rem;letter-spacing:0.04em;"><strong>TRIGGER</strong></mark> `;
}

/**
 * Walk text nodes inside `root` and replace bracket-label tokens with
 * `<mark>` highlights followed by the original bracket. Bold any remaining
 * `[X]` placeholder text (e.g. `[Address]`, `[Current Bill Amount]`) so
 * variables stand out in the live lane. Skips text inside existing
 * `<mark>`, `<strong>`, `<a>`, or label spans we just inserted.
 */
function walkAndDecorate(root: HTMLElement): void {
  const skipTags = new Set(["MARK", "STRONG", "A", "CODE", "SCRIPT", "STYLE"]);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      let parent = node.parentElement;
      while (parent && parent !== root) {
        if (skipTags.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
        parent = parent.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const textNodes: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) textNodes.push(n as Text);

  for (const tn of textNodes) {
    const original = tn.nodeValue || "";
    if (!original) continue;

    let mutated = escapeHtml(original);
    let changed = false;

    // Label bracket replacements (longest-first to avoid partial matches).
    const sortedSpecs = [...LABEL_SPECS].sort((a, b) => b.bracket.length - a.bracket.length);
    for (const spec of sortedSpecs) {
      const escapedBracket = escapeHtml(spec.bracket);
      if (mutated.includes(escapedBracket)) {
        mutated = mutated.split(escapedBracket).join(buildLabelMark(spec) + escapedBracket);
        changed = true;
      }
    }

    // Trigger: at start of node text (paragraph-leading)
    if (TRIGGER_RE.test(original)) {
      mutated = mutated.replace(/^(\s*)Trigger:/i, (_m, ws) => `${ws}${buildTriggerMark()}Trigger:`);
      changed = true;
    }

    // Bold remaining bracket placeholders (not in our label set).
    mutated = mutated.replace(/\[([^\[\]\n]{1,80})\]/g, (m, inner: string) => {
      const norm = `[${inner}]`.toLowerCase();
      if (LABEL_BRACKETS.has(norm)) return m;
      // Skip if this is the trailing bracket from a label we already wrapped
      // (it lives inside the same text content immediately after the mark).
      changed = true;
      return `<strong>[${escapeHtml(inner)}]</strong>`;
    });

    if (!changed) continue;

    const span = document.createElement("span");
    span.innerHTML = mutated;
    const parent = tn.parentNode;
    if (!parent) continue;
    while (span.firstChild) parent.insertBefore(span.firstChild, tn);
    parent.removeChild(tn);
  }
}

/**
 * Walk text nodes and replace em/en dashes with " - " for display only.
 * Source storage is untouched.
 */
function stripDashes(root: HTMLElement): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let n: Node | null;
  const nodes: Text[] = [];
  while ((n = walker.nextNode())) nodes.push(n as Text);
  for (const tn of nodes) {
    const v = tn.nodeValue || "";
    if (v.includes("\u2014") || v.includes("\u2013")) {
      tn.nodeValue = v.replace(/\s*[\u2014\u2013]\s*/g, " - ");
    }
  }
}

/**
 * Detect consecutive sibling <p> elements whose visible text starts with
 * a bullet marker (-, •, *, ·) or a numbered marker (1., 1), a)) and
 * fold them into a <ul>/<ol>. Pure render-time grouping — no source text
 * is modified beyond stripping the marker character.
 */
function buildLists(root: HTMLElement): void {
  const paras = Array.from(root.querySelectorAll(":scope > p, :scope div > p")) as HTMLParagraphElement[];
  if (paras.length === 0) return;

  const bulletRe = /^\s*([-*•·▪◦])\s+(.+)$/s;
  const numberedRe = /^\s*(\d{1,3}|[a-zA-Z])[.)]\s+(.+)$/s;

  type Marker = { kind: "ul" | "ol"; text: string };
  const classify = (p: HTMLParagraphElement): Marker | null => {
    const txt = (p.textContent || "").trim();
    if (!txt) return null;
    const b = txt.match(bulletRe);
    if (b) return { kind: "ul", text: b[2] };
    const o = txt.match(numberedRe);
    if (o) return { kind: "ol", text: o[2] };
    return null;
  };

  let i = 0;
  while (i < paras.length) {
    const m = classify(paras[i]);
    if (!m) { i++; continue; }
    // collect run of same-kind paragraphs that are direct siblings
    const run: HTMLParagraphElement[] = [paras[i]];
    let j = i + 1;
    while (j < paras.length) {
      const next = paras[j];
      if (next.parentElement !== paras[i].parentElement) break;
      // ensure no non-empty sibling between them
      let between: ChildNode | null = run[run.length - 1].nextSibling;
      let bad = false;
      while (between && between !== next) {
        if (between.nodeType === 1) { bad = true; break; }
        if (between.nodeType === 3 && (between.nodeValue || "").trim()) { bad = true; break; }
        between = between.nextSibling;
      }
      if (bad) break;
      const nm = classify(next);
      if (!nm || nm.kind !== m.kind) break;
      run.push(next);
      j++;
    }
    if (run.length >= 2) {
      const list = document.createElement(m.kind);
      list.setAttribute("data-script-list", "auto");
      list.style.cssText = "margin:0.5em 0;padding-left:1.5em;";
      for (const p of run) {
        const cl = classify(p)!;
        const li = document.createElement("li");
        li.style.cssText = "margin:0.15em 0;";
        // Use innerHTML of original <p> minus the leading marker
        const html = p.innerHTML;
        const stripped = html.replace(/^\s*([-*•·▪◦]|\d{1,3}[.)]|[a-zA-Z][.)])\s+/, "");
        li.innerHTML = stripped;
        list.appendChild(li);
      }
      run[0].parentElement!.insertBefore(list, run[0]);
      run.forEach((p) => p.remove());
    }
    i = j;
  }
}

/**
 * Optional live-data bag for bracket placeholder substitution at render
 * time. Keys are case-insensitive bracket labels (e.g. `address`,
 * `customer name`, `current bill amount`) and values are the resolved
 * string to inject. Falsy/empty values fall back to the original bracket.
 */
export interface ScriptLiveValues {
  [bracket: string]: string | number | null | undefined;
}

/**
 * Build a bracket label -> resolved value map from a lead/deal context.
 * Bracket keys are normalized lower-case (no spaces/punctuation) so the
 * decorator can compare across whitespace variations.
 */
export function buildLiveValuesFromLead(
  lead: Record<string, any> | null | undefined,
  deal?: Record<string, any> | null,
): ScriptLiveValues {
  if (!lead && !deal) return {};
  const fullName = [lead?.first_name, lead?.last_name].filter(Boolean).join(" ").trim();
  const addr = [lead?.address, lead?.city, lead?.state, lead?.zip].filter(Boolean).join(", ");
  const bill = lead?.monthly_electric_bill;
  const out: ScriptLiveValues = {
    "customer name": fullName,
    "customer first name": lead?.first_name || "",
    "first name": lead?.first_name || "",
    "last name": lead?.last_name || "",
    "name": fullName,
    "address": addr || lead?.address || "",
    "property address": addr || lead?.address || "",
    "city": lead?.city || "",
    "state": lead?.state || "",
    "zip": lead?.zip || "",
    "phone": lead?.phone || "",
    "email": lead?.email || "",
    "current bill amount": bill != null ? `$${bill}` : "",
    "current electric bill": bill != null ? `$${bill}` : "",
    "monthly bill": bill != null ? `$${bill}` : "",
    "monthly electric bill": bill != null ? `$${bill}` : "",
    "square footage": lead?.square_footage != null ? String(lead.square_footage) : "",
    "home size": lead?.square_footage != null ? `${lead.square_footage} sq ft` : "",
    "property type": lead?.property_type || "",
    "ac age": lead?.hvac_age != null ? `${lead.hvac_age} years` : "",
    "hvac age": lead?.hvac_age != null ? `${lead.hvac_age} years` : "",
    "ac type": lead?.hvac_type || "",
    "hvac type": lead?.hvac_type || "",
    "deal amount": deal?.amount != null ? `$${deal.amount}` : "",
    "loan amount": deal?.amount != null ? `$${deal.amount}` : "",
    "monthly payment": deal?.monthly_payment != null ? `$${deal.monthly_payment}` : "",
  };
  return out;
}

function normalizeBracket(s: string): string {
  return s.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Replace bracket placeholders like `[Address]`, `[Customer Name]`,
 * `[Current Bill Amount]` with their live values, wrapped in
 * `<strong class="script-live-var">…</strong>` so they stand out from the
 * surrounding script copy. Brackets without a matching value are left
 * untouched (and will later be bolded by walkAndDecorate). The label
 * bracket set (`[Rep]`, `[Customer Response]`, …) is preserved verbatim
 * so the label decorator can still attach the SAY THIS / WAIT / etc.
 * marks afterward.
 */
function substituteLiveBrackets(root: HTMLElement, values: ScriptLiveValues): void {
  const skipTags = new Set(["MARK", "STRONG", "A", "CODE", "SCRIPT", "STYLE"]);
  const labelBrackets = new Set(LABEL_SPECS.map((s) => s.bracket.toLowerCase()));

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      let parent = node.parentElement;
      while (parent && parent !== root) {
        if (skipTags.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
        parent = parent.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) nodes.push(n as Text);

  for (const tn of nodes) {
    const raw = tn.nodeValue || "";
    if (!raw.includes("[")) continue;
    let changed = false;
    const replaced = raw.replace(/\[([^\[\]\n]{1,80})\]/g, (match, inner: string) => {
      const fullLower = `[${inner}]`.toLowerCase();
      if (labelBrackets.has(fullLower)) return match;
      const key = normalizeBracket(inner);
      const val = values[key];
      if (val == null || val === "") return match;
      changed = true;
      const esc = String(val).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      return `<strong class="script-live-var" style="background-color:#fef9c3;padding:0 3px;border-radius:3px;">${esc}</strong>`;
    });
    if (!changed) continue;
    const span = document.createElement("span");
    span.innerHTML = replaced;
    const parent = tn.parentNode;
    if (!parent) continue;
    while (span.firstChild) parent.insertBefore(span.firstChild, tn);
    parent.removeChild(tn);
  }
}

/**
 * Decorate a script HTML fragment with the SAY THIS / WAIT / DO THIS /
 * TRIGGER / RETURN TO / NEXT / AGENT ONLY highlight tokens, bold any
 * bracket placeholders, normalize em/en dashes to " - ", and fold
 * marker-led paragraphs into bullet/numbered lists. When `liveValues` is
 * provided, bracket placeholders matching live keys are first replaced
 * with the resolved value (highlighted yellow) so the rep sees the
 * customer's actual data inline. All transforms are render-time only;
 * source storage is untouched.
 */
export function decorateScriptLabels(html: string, liveValues?: ScriptLiveValues): string {
  if (!html || typeof window === "undefined") return html;
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  stripDashes(wrapper);
  buildLists(wrapper);
  if (liveValues && Object.keys(liveValues).length > 0) {
    substituteLiveBrackets(wrapper, liveValues);
  }
  walkAndDecorate(wrapper);
  markCustomerBlocks(wrapper);
  return wrapper.innerHTML;
}
